// "Jobs that match" on the candidate page (Matt, 2026-10-23): live postings from job-distribution for the candidate's
// discipline and the states they are licensed in, through the CRM's own /api/jobs (functions/api/jobs.js holds the
// token). Ten rows. Any error hides the box -- it never says "no jobs" when it simply could not ask.

// NIGHT-DIRECTSTAFF-2026-10-23c §2 (the distribution window's new filters): disciplines, states = licensed + compact-privilege states,
// + every issuing state of their compact when a license is in one (compact-eligible: the same rule as the agents' facility agent),
// start_after = their available date when it is today or later, sort=start (soonest start first), ten rows; each row says why.
import { COMPACT_STATES } from '../generated/compact';

const DISCIPLINES = ['PT', 'PTA', 'OT', 'COTA', 'SLP', 'SLPA'];

/** The candidate's discipline as the API knows it, or null (blank, Unknown, anything else: no box). */
export function disciplineOf(candidate) {
  const d = String((candidate && candidate.specialty) || '').trim().toUpperCase();
  return DISCIPLINES.includes(d) ? d : null;
}

/**
 * The states to search: every license / compact privilege on file (their onboarding documents and credentials), else
 * the home state, said as such. -> { states: ['CO', ...], from: 'licenses' | 'home' | 'none' }
 */
export function statesOf(candidate, credentialRows = []) {
  const st = (d) => String(d.license_state || '').trim().toUpperCase();
  const of = (kind) => [...new Set((credentialRows || []).filter((d) => d.kind === kind && /^[A-Z]{2}$/.test(st(d))).map(st))].sort();
  const licensed = of('license'); const privilege = of('compact_privilege');
  const why = {};
  for (const s of privilege) why[s] = { kind: 'privilege' };
  for (const s of licensed) why[s] = { kind: 'licensed' };
  // compact-eligible: a license in an issuing state of their discipline's compact -> every other issuing state
  const issuing = COMPACT_STATES[disciplineOf(candidate) || ''] || [];
  const via = licensed.find((s) => issuing.includes(s));
  if (via) for (const s of issuing) if (!why[s]) why[s] = { kind: 'compact', via };
  let states = Object.keys(why).sort();
  // NIGHT-DIRECTSTAFF-2026-10-24 §1: the states they told us they want, where they can work -- when the two overlap
  const wants = Array.isArray(candidate && candidate.preferred_states) ? candidate.preferred_states.map((x) => String(x).toUpperCase()) : [];
  const both = states.filter((x) => wants.includes(x));
  if (both.length) states = both;
  if (states.length) return { states, from: both.length ? 'preferred' : 'licenses', why, compactVia: via || null };
  const home = String((candidate && candidate.state) || '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(home) ? { states: [home], from: 'home', why: { [home]: { kind: 'home' } }, compactVia: null } : { states: [], from: 'none', why: {}, compactVia: null };
}

/** Their available date when it is today or later -- NIGHT-DIRECTSTAFF-2026-10-24 §1: what they told us on their availability page
 *  (candidates.available_from) first, else the "available:YYYY-MM-DD" tag; null = available now (or not set). */
export function availableFrom(candidate, now = new Date()) {
  const tag = ((candidate && candidate.tags) || []).find((t) => typeof t === 'string' && /^available:\d{4}-\d{2}-\d{2}$/.test(t));
  const col = candidate && typeof candidate.available_from === 'string' && /^\d{4}-\d{2}-\d{2}/.test(candidate.available_from) ? candidate.available_from.slice(0, 10) : null;
  const d = col || (tag ? tag.slice('available:'.length) : null);
  const today = new Date(now).toISOString().slice(0, 10);
  return d && d >= today ? d : null;
}

const STATE_WHY = { licensed: (s) => `licensed in ${s}`, privilege: (s) => `compact privilege in ${s}`, compact: (s, w) => `compact-eligible in ${s} (${w.via} license)`,
  home: (s) => `home state ${s}` };
/** Why a row matched: "licensed in CO · starts Nov 9 (free from Nov 1)" -- the state, then the start */
export function matchWhy(job, why = {}, startAfter = null) {
  const s = String((job && job.state) || '').toUpperCase(); const w = why[s];
  const where = w ? STATE_WHY[w.kind](s, w) : (s ? `in ${s}` : '');
  const iso = job && job.start_date_iso;
  const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const start = iso ? `starts ${fmt(iso)}${startAfter ? ` (free from ${fmt(startAfter)})` : ''}` : (job && job.start_date ? 'ASAP start' : '');
  return [where, start].filter(Boolean).join(' · ');
}

/** GET /api/jobs with the staff session -> { total, jobs } ; throws on any error (the box then hides). */
export async function fetchMatchingJobs({ supabase, discipline, states, startAfter = null, fetchFn = (...a) => fetch(...a) }) {
  let token = '';
  try { const { data } = await supabase.auth.getSession(); token = (data && data.session && data.session.access_token) || ''; } catch (e) { token = ''; }
  if (!token) throw new Error('signed_out');
  const q = new URLSearchParams({ disciplines: discipline });
  if (states.length) q.set('states', states.join(','));
  if (startAfter) q.set('start_after', startAfter);
  q.set('sort', 'start');
  const res = await fetchFn(`/api/jobs?${q}`, { headers: { authorization: `Bearer ${token}` } });
  const out = await res.json().catch(() => null);
  if (!res.ok || !out || !Array.isArray(out.jobs)) throw new Error((out && out.error) || `http_${res.status}`);
  return { total: Number(out.total) || out.jobs.length, jobs: out.jobs.slice(0, 10) };
}
